import { useQuery } from "@tanstack/react-query";
import type {
    TApiODIN,
    TResponseGetConflictExtractions,
    TResponseGetExtraction,
    TResponseGetLabelset,
    TResponseGetOverlappingPeriods,
    TResponseGetStandardisedExtraction,
    TExtractionType,
} from "@/apis/odin/apiODIN";
import { extractionKeys } from "@/queries/queryKeys";
import { convertAPIResponseToDTOTableExtraction } from "@/store/tableExtractions/convertAPIResponseToDTOTableExtraction";
import { convertAPIResponseToDTOStandardizedTableExtraction } from "@/store/tableExtractions/convertAPIResponseToDTOStandardizedTableExtraction";

const buildExtractionQueryData = (response: TResponseGetExtraction) => ({
    raw: response,
    dto: convertAPIResponseToDTOTableExtraction(response),
});

const buildStandardizedExtractionQueryData = ({
    extraction,
    overlappingPeriods = [],
}: {
    extraction: TResponseGetStandardisedExtraction;
    overlappingPeriods?: TResponseGetOverlappingPeriods;
}) => ({
    raw: extraction,
    dto: convertAPIResponseToDTOStandardizedTableExtraction(
        extraction,
        overlappingPeriods,
    ),
});

export const createExtractionQueryHooks = ({
    apiODIN,
}: {
    apiODIN: TApiODIN;
}) => {
    const getExtractionQueryOptions = (extractionId: number) => ({
        queryKey: extractionKeys.raw(extractionId),
        queryFn: async () => {
            const response = await apiODIN.getExtraction(extractionId);
            return buildExtractionQueryData(response.data);
        },
    });

    const useExtractionQuery = (
        extractionId: number | null,
        options?: { enabled?: boolean },
    ) =>
        useQuery({
            ...(extractionId === null
                ? {
                      queryKey: extractionKeys.raw(-1),
                      queryFn: () => {
                          throw new Error("extractionId is required");
                      },
                  }
                : getExtractionQueryOptions(extractionId)),
            enabled: (options?.enabled ?? true) && extractionId !== null,
        });

    const getStandardizedExtractionQueryOptions = ({
        extractionId,
        projectId,
        databaseId,
    }: {
        extractionId: number;
        projectId: number;
        databaseId: number;
    }) => ({
        queryKey: extractionKeys.standardizedDetail(
            extractionId,
            projectId,
            databaseId,
        ),
        queryFn: async () => {
            const extraction =
                await apiODIN.getStandardizedExtraction(extractionId);
            return buildStandardizedExtractionQueryData({
                extraction: extraction.data,
            });
        },
    });

    const getOverlappingPeriodsQueryOptions = ({
        extractionId,
        projectId,
        databaseId,
    }: {
        extractionId: number;
        projectId: number;
        databaseId: number;
    }) => ({
        queryKey: extractionKeys.overlappingPeriodsDetail(
            extractionId,
            projectId,
            databaseId,
        ),
        queryFn: async (): Promise<TResponseGetOverlappingPeriods> => {
            const overlappingPeriods =
                await apiODIN.getOverlappingPeriodsByDealIdAndExtractionId({
                    projectId,
                    databaseId,
                    extractionId,
                });
            return overlappingPeriods.data;
        },
    });

    const useStandardizedExtractionQuery = (
        params: {
            extractionId: number | null;
            projectId: number | null;
            databaseId: number | null;
        },
        options?: { enabled?: boolean },
    ) => {
        const { extractionId, projectId, databaseId } = params;
        const ready =
            extractionId !== null && projectId !== null && databaseId !== null;
        return useQuery({
            queryKey: extractionKeys.standardizedDetail(
                extractionId ?? -1,
                projectId ?? -1,
                databaseId ?? -1,
            ),
            queryFn: () => {
                if (!ready) {
                    throw new Error(
                        "standardized query: missing extraction context",
                    );
                }
                return getStandardizedExtractionQueryOptions({
                    extractionId,
                    projectId,
                    databaseId,
                }).queryFn();
            },
            enabled: (options?.enabled ?? true) && ready,
        });
    };

    const useOverlappingPeriodsQuery = (
        params: {
            extractionId: number | null;
            projectId: number | null;
            databaseId: number | null;
        },
        options?: { enabled?: boolean },
    ) => {
        const { extractionId, projectId, databaseId } = params;
        const ready =
            extractionId !== null && projectId !== null && databaseId !== null;
        return useQuery({
            queryKey: extractionKeys.overlappingPeriodsDetail(
                extractionId ?? -1,
                projectId ?? -1,
                databaseId ?? -1,
            ),
            queryFn: () => {
                if (!ready) {
                    throw new Error(
                        "overlapping periods query: missing extraction context",
                    );
                }
                return getOverlappingPeriodsQueryOptions({
                    extractionId,
                    projectId,
                    databaseId,
                }).queryFn();
            },
            enabled: (options?.enabled ?? true) && ready,
        });
    };

    const getLabelsetQueryOptions = (extractionId: number) => ({
        queryKey: extractionKeys.labelset(extractionId),
        queryFn: async (): Promise<TResponseGetLabelset> => {
            const response = await apiODIN.getLabelset(extractionId);
            return response.data;
        },
    });

    const useLabelsetQuery = (
        extractionId: number | null,
        options?: { enabled?: boolean },
    ) =>
        useQuery({
            ...(extractionId === null
                ? {
                      queryKey: extractionKeys.labelset(-1),
                      queryFn: () => {
                          throw new Error("extractionId is required");
                      },
                  }
                : getLabelsetQueryOptions(extractionId)),
            enabled: (options?.enabled ?? true) && extractionId !== null,
        });

    const getExtractionTypeQueryOptions = (extractionId: number) => ({
        queryKey: extractionKeys.extractionType(extractionId),
        queryFn: async (): Promise<TExtractionType> => {
            const response = await apiODIN.getExtractionType(extractionId);
            return response.data;
        },
    });

    const useExtractionTypeQuery = (
        extractionId: number | null,
        options?: { enabled?: boolean },
    ) =>
        useQuery({
            ...(extractionId === null
                ? {
                      queryKey: extractionKeys.extractionType(-1),
                      queryFn: () => {
                          throw new Error("extractionId is required");
                      },
                  }
                : getExtractionTypeQueryOptions(extractionId)),
            enabled: (options?.enabled ?? true) && extractionId !== null,
        });

    const getConflictExtractionsQueryOptions = ({
        extractionId,
        projectId,
        databaseId,
    }: {
        extractionId: number;
        projectId: number;
        databaseId: number;
    }) => ({
        queryKey: extractionKeys.conflictsDetail(
            extractionId,
            projectId,
            databaseId,
        ),
        queryFn: async (): Promise<TResponseGetConflictExtractions> => {
            const response =
                await apiODIN.getConflictExtractionsByDealIdAndExtractionId({
                    extractionId,
                    projectId,
                    databaseId,
                });
            return response.data;
        },
    });

    const useConflictExtractionsQuery = (
        params: {
            extractionId: number | null;
            projectId: number | null;
            databaseId: number | null;
        },
        options?: { enabled?: boolean },
    ) => {
        const { extractionId, projectId, databaseId } = params;
        const ready =
            extractionId !== null && projectId !== null && databaseId !== null;
        return useQuery({
            queryKey: extractionKeys.conflictsDetail(
                extractionId ?? -1,
                projectId ?? -1,
                databaseId ?? -1,
            ),
            queryFn: () => {
                if (!ready) {
                    throw new Error(
                        "conflicts query: missing extraction context",
                    );
                }
                return getConflictExtractionsQueryOptions({
                    extractionId,
                    projectId,
                    databaseId,
                }).queryFn();
            },
            enabled: (options?.enabled ?? true) && ready,
        });
    };

    const useOperatingStatementPeriodsQuery = (
        params: {
            projectId: number | null;
            databaseId: number | null;
            annualizationType: string;
        },
        options?: { enabled?: boolean },
    ) => {
        const { projectId, databaseId, annualizationType } = params;
        const ready = projectId !== null && databaseId !== null;
        return useQuery({
            queryKey: extractionKeys.operatingStatementPeriods(
                projectId ?? -1,
                databaseId ?? -1,
                annualizationType,
            ),
            queryFn: async () => {
                if (!ready) throw new Error("missing ids");
                const response = await apiODIN.getOperatingStatementPeriods({
                    projectId,
                    databaseId,
                    annualizationType: annualizationType as Parameters<
                        TApiODIN["getOperatingStatementPeriods"]
                    >[0]["annualizationType"],
                });
                return response.data;
            },
            enabled: (options?.enabled ?? true) && ready,
        });
    };

    const useOperatingStatementQuery = (
        params: {
            projectId: number;
            databaseId: number;
            startDate: string | null;
            endDate: string | null;
            interval: number;
            reconciliation?: boolean;
            annualizationType: Parameters<
                TApiODIN["getOperatingStatementByDealId"]
            >[0]["annualizationType"];
        },
        options?: { enabled?: boolean },
    ) => {
        const {
            projectId,
            databaseId,
            startDate,
            endDate,
            interval,
            annualizationType,
            reconciliation,
        } = params;
        const ready = startDate !== null && endDate !== null;
        return useQuery({
            queryKey: extractionKeys.operatingStatement(projectId, databaseId, {
                startDate,
                endDate,
                interval,
                annualizationType,
                reconciliation,
            }),
            queryFn: async () => {
                if (!ready) throw new Error("missing date range");
                const response = await apiODIN.getOperatingStatementByDealId({
                    projectId,
                    databaseId,
                    startDate,
                    endDate,
                    interval,
                    annualizationType,
                    reconciliation,
                });
                return response.data ?? null;
            },
            enabled: (options?.enabled ?? true) && ready,
        });
    };

    return {
        useExtractionQuery,
        useStandardizedExtractionQuery,
        useOverlappingPeriodsQuery,
        useLabelsetQuery,
        useExtractionTypeQuery,
        useConflictExtractionsQuery,
        useOperatingStatementPeriodsQuery,
        useOperatingStatementQuery,
        getExtractionQueryOptions,
        getStandardizedExtractionQueryOptions,
        getOverlappingPeriodsQueryOptions,
        getLabelsetQueryOptions,
        getExtractionTypeQueryOptions,
        getConflictExtractionsQueryOptions,
    };
};

export type TExtractionQueryHooks = ReturnType<
    typeof createExtractionQueryHooks
>;
export { buildStandardizedExtractionQueryData };
